/* ============================================================
   Non-blocking email capture — interactions + analytics.
   Self-contained custom element; no theme-internal deps.
   - One tab toggles one panel (right-slide on desktop, up-slide on mobile).
   - Dismissal persists in localStorage for `data-dismiss-days`.
   - Pushes funnel events to dataLayer (the popup had ZERO
     instrumentation; this finally gives a capture-rate baseline).
   ============================================================ */
(function () {
  var STORE_KEY = 'sihman:ec_dismissed';

  function track(event, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, detail || {}));
  }

  function isDismissed(days) {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (!ts) return false;
      var ageDays = (Date.now() - ts) / 86400000;
      return ageDays < days;
    } catch (e) {
      return false;
    }
  }

  function setDismissed() {
    try { localStorage.setItem(STORE_KEY, String(Date.now())); } catch (e) {}
  }

  if (!customElements.get('email-capture')) {
    customElements.define('email-capture', class EmailCapture extends HTMLElement {
      connectedCallback() {
        var designMode = window.Shopify && window.Shopify.designMode;
        var days = parseInt(this.dataset.dismissDays, 10) || 14;

        if (!designMode && isDismissed(days)) {
          // stay hidden; still allow explicit re-open via anchors
          this._bindOpeners(true);
          return;
        }

        this.removeAttribute('hidden');
        this._bindCore();
        this._bindOpeners(false);
        this._enableAnim();

        track('email_capture_view', { ec_variant: 'sidetab' });
      }

      _enableAnim() {
        // Enable CSS transitions only AFTER the closed state has painted, so the
        // panel/tab never animate-in on first load (the async stylesheet + this
        // JS un-hiding the element otherwise race the page CSS, making the widget
        // visibly slide into place on every navigation). Two rAFs guarantee a
        // committed paint of the closed state before `.ec-anim` is added.
        var self = this;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { self.classList.add('ec-anim'); });
        });
      }

      _bindCore() {
        var self = this;
        this._tab = this.querySelector('.ec-tab');
        this._panelClose = this.querySelector('.ec-panel__close');
        this._opened = false;

        if (this._tab) {
          this._tab.addEventListener('click', function () { self.toggle(); });
        }
        if (this._panelClose) {
          this._panelClose.addEventListener('click', function () { self.close(); });
        }

        // click outside the widget closes the open panel
        document.addEventListener('click', function (e) {
          if (!self.classList.contains('ec-open')) return;
          if (self.contains(e.target)) return;
          self.close();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.classList.contains('ec-open')) self.close();
        });

        // submit tracking on the reused newsletter form
        this.querySelectorAll('form').forEach(function (f) {
          f.addEventListener('submit', function () {
            track('email_capture_submit', { ec_surface: 'panel' });
          });
        });
      }

      _bindOpeners(hiddenStart) {
        var self = this;
        // announcement-bar / footer links can pop the widget open on demand
        document.addEventListener('click', function (e) {
          var link = e.target.closest('a[href="#open-email-capture"], a[href="#open-discount-popup"]');
          if (!link) return;
          e.preventDefault();
          if (hiddenStart && self.hasAttribute('hidden')) {
            self.removeAttribute('hidden');
            self._bindCore();
            self._enableAnim();
            hiddenStart = false;
          }
          self.open();
        });
      }

      open() {
        this.classList.add('ec-open');
        if (this._tab) this._tab.setAttribute('aria-expanded', 'true');
        if (!this._opened) { this._opened = true; track('email_capture_open', {}); }
        var input = this.querySelector('.ec-panel input[type="email"]');
        if (input) { try { input.focus(); } catch (e) {} }
      }

      close() {
        this.classList.remove('ec-open');
        if (this._tab) this._tab.setAttribute('aria-expanded', 'false');
      }

      toggle() {
        if (this.classList.contains('ec-open')) { this.close(); }
        else { this.open(); }
      }
    });
  }
})();
